// SDK client auth configuration (Web)
// Uses cookie-based authentication (browser flow with Kratos)

import { client } from '@common/api/familypal/client.gen';
import { applyOpenflameDevRouting } from '@common/api/openflameDev';

// Configure the client to send cookies with all requests
// This works with the Oathkeeper proxy that validates session cookies
client.setConfig({
  credentials: 'include',
});

// Ensure cookies are sent with all requests
client.interceptors.request.use(async (request) => {
  // No need to set auth headers - cookies are sent automatically
  return request;
});

// In an OpenFlame workspace run, route API calls to the developer's service
// dev runs (x-openflame-dev header). No-op in production builds.
applyOpenflameDevRouting(client);

// Re-export from common
export { client };
export type { MediaFile } from '@common/media/types';
export { getMediaFiles, uploadMediaFile, uploadProfilePhoto } from '@common/media/helpers';

// Subscription API helpers
const API_BASE = 'https://api.familypal.com/familypal/v1/identity';

export interface SubscriptionStatus {
  subscription_id: string;
  status: string; // "active" | "cancelled" | "expired" | "billing_retry" | "none"
  product_id: string;
  platform: string;
  expires_at: string;
  is_trial: boolean;
  original_transaction_id: string;
}

export async function getSubscriptionStatus(familyId: string): Promise<SubscriptionStatus> {
  // Always query by family_id. The user.id from Kratos (identity UUID) does NOT match
  // the user table UUID stored in the subscription table, so querying by user_id from
  // the web app will never find mobile subscriptions.
  const params = new URLSearchParams();
  params.set('family_id', familyId);
  const resp = await fetch(`${API_BASE}/subscription/status?${params.toString()}`, {
    credentials: 'include',
  });
  if (!resp.ok) {
    throw new Error(`Failed to fetch subscription status: ${resp.status} ${resp.statusText}`);
  }
  return resp.json();
}

export interface FamilySubscriptionStatus {
  family_id?: string;
  familyId?: string;
  family_name?: string;
  familyName?: string;
  status: string;
  is_active?: boolean;
  isActive?: boolean;
}

export async function getFamilySubscriptionStatuses(): Promise<FamilySubscriptionStatus[]> {
  const resp = await fetch(`${API_BASE}/subscription/family-status`, {
    credentials: 'include',
  });
  if (!resp.ok) {
    throw new Error(`Failed to fetch family subscription statuses: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data.families || [];
}

export async function createCheckoutSession(params: {
  family_id: string;
  price_id: string;
  success_url: string;
  cancel_url: string;
}): Promise<{ checkout_url: string; session_id: string }> {
  const resp = await fetch(`${API_BASE}/subscription/checkout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!resp.ok) {
    throw new Error(`Failed to create checkout session: ${resp.status}`);
  }
  return resp.json();
}
